/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    createContext,
    use,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
    Suspense,
    type ComponentPropsWithoutRef,
    type ReactElement,
    type ReactNode,
} from 'react';
import { Await } from 'react-router';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import { useAnalytics } from '@/hooks/use-analytics';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { transformHtmlImageUrls } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { DynamicImage } from '@/components/dynamic-image';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { useSubCategory } from '@/components/navigation-menu/context';
import { useHeaderScroll } from '@/components/header';
// RGBARRY-CUSTOMIZATION: use the current brand-approved artwork in each desktop mega-menu flyout.
import navSaleBanner from '/images/nav-sale-banner.jpeg';
import navImageNowTrending from '/images/nav-image-now-trending.jpeg';
import navImageShopBags from '/images/nav-image-shop-bags.jpeg';
import navImageTravel from '/images/nav-image-travel.jpeg';
import navImageShopByCollection from '/images/nav-image-shop-by-collection.jpeg';
import navImageAntiTheft from '/images/nav-image-anti-theft.jpeg';
import { routes, routeHref } from '@/route-paths';
import { CategoryHrefProvider, useCategoryHref, type CategoryUrlMap } from '@/components/navigation-menu/category-href';
import { readSeoUrl } from '@/lib/product/seo-url';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';
import { getRegionIds } from '@/lib/decorators/region-definition';
import { EmbeddedComponentRegion } from '@/components/region/embedded-component-region';
import type { ComponentWithComponentData } from '@/lib/page-designer/component-loader.server';

@Component('megaMenu', {
    name: 'Mega Menu',
    group: 'Layout',
    description: 'Site-wide mega menu with per-category dropdown panel content slots',
    embedded: true,
    component_id: 'mega-menu',
})
@RegionDefinition([
    // One region per top-level category, keyed by category id via the `region_<id>` convention.
    // Edit this list to match your catalog's top-level category ids. Must stay an array literal —
    // the cartridge generator extracts regions via AST and only handles array-literal arguments.
    { id: 'region_women', name: 'Women' },
    { id: 'region_men', name: 'Men' },
    { id: 'region_kids', name: 'Kids' },
])
// oxlint-disable-next-line react-refresh/only-export-components
export class MegaMenuMetadata {}

/**
 * Declared mega-menu region ids, derived from the `@RegionDefinition` decorator above so the
 * decorator list is the single source of truth. A top-level category renders its region when
 * `region_${category.id}` is present in this set.
 */
export const MEGA_MENU_REGION_IDS: ReadonlySet<string> = new Set(getRegionIds(MegaMenuMetadata));

/**
 * Resolves the embedded-component region id for a top-level category, or `undefined` when the
 * category has no mapped region (no embedded component, no category id, or the derived
 * `region_${categoryId}` is not declared in {@link MEGA_MENU_REGION_IDS}). Pure and exported so the
 * per-category mapping can be unit-tested without mounting the lazily-rendered dropdown panel.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function resolveMegaMenuRegionId(
    categoryId: string | undefined,
    hasEmbeddedComponent: boolean,
    regionIds: ReadonlySet<string> = MEGA_MENU_REGION_IDS
): string | undefined {
    if (!hasEmbeddedComponent || categoryId === undefined) return undefined;
    const regionId = `region_${categoryId}`;
    return regionIds.has(regionId) ? regionId : undefined;
}

interface MobileMenuContextType {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
    categories: ShopperProducts.schemas['Category'][];
}

const MobileMenuContext = createContext<MobileMenuContextType | null>(null);

// oxlint-disable-next-line react-refresh/only-export-components
export function useMobileMenu() {
    return useContext(MobileMenuContext);
}

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return typeof category?.c_headerMenuBanner === 'string' && category?.c_headerMenuBanner?.length > 0;
}

/**
 * RGBARRY-CUSTOMIZATION (W-23789028, slice 4): builds the `nav_click` hierarchy payload from an
 * ordered list of category labels (top-level first). The GTM container's nav_click trigger reads
 * `nav_level_1..3`, so only the first three non-empty labels are forwarded.
 */
function toNavLevels(labels: Array<string | undefined>): {
    navLevel1: string;
    navLevel2?: string;
    navLevel3?: string;
} {
    const [l1, l2, l3] = labels.map((l) => l?.trim()).filter((l): l is string => Boolean(l));
    return { navLevel1: l1 ?? '', navLevel2: l2, navLevel3: l3 };
}

/** Static nav banner data mirroring baggallini.com mega menu images, used when SFCC catalog
 *  attributes (c_headerMenuBanner / c_slotBannerImage) are not populated on the demo instance. */
// RGBARRY-CUSTOMIZATION: keep each brand-approved promotional card (image, copy, and destination)
// assigned to the requested top-level navigation category.
const NAV_BANNERS: Record<string, { imageUrl: string; caption: string; altText: string; href: string }> = {
    'now trending': {
        imageUrl: navImageNowTrending,
        caption: 'Meet Coco X Baggallini',
        altText: 'Now Trending — Meet Coco X Baggallini',
        href: '/cocorocha',
    },
    'shop bags': {
        imageUrl: navImageShopBags,
        caption: 'Carry a lot or a little: Shop Crossbody Styles',
        altText: 'Shop Bags — Crossbody styles',
        href: '/crossbody-handbags/',
    },
    travel: {
        imageUrl: navImageTravel,
        caption: 'Weekend Getaway Ready: Shop Carry-on Compliant',
        altText: 'Travel — Carry-on compliant bags',
        href: '/womens-carry-on-bags/',
    },
    'shop by collection': {
        imageUrl: navImageShopByCollection,
        caption: 'Meet Coco X Baggallini',
        altText: 'Shop By Collection — Meet Coco X Baggallini',
        href: '/cocorocha',
    },
    'anti-theft': {
        imageUrl: navImageAntiTheft,
        caption: 'Carry with Confidence, In Style: Shop The Jet Set Capsule',
        altText: 'Anti-Theft — Jet Set capsule',
        href: '/jet-set/',
    },
    sale: {
        imageUrl: navSaleBanner,
        caption: 'Shop Sale — Fresh Styles, Unbeatable Prices.',
        altText: 'Sale — Shop now',
        href: '/sale/shop-by-category/shop-all/',
    },
};
// END RGBARRY-CUSTOMIZATION

// RGBARRY-CUSTOMIZATION (perf): mega-menu banner is capped at max-h-[512px] and not above-the-fold
// on initial load (only visible once a shopper opens the dropdown), so it's DIS-downscaled with
// lazy loading rather than the LCP treatment given to the PLP category banner. 384px box (fixed
// ~380px grid column, `md:grid-cols-[1fr_380px]`); DynamicImage adds the 2× retina candidate itself.
const MEGA_MENU_BANNER_IMAGE_WIDTHS = { base: 384 };

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig();
    const categoryHref = useCategoryHref();
    const imageSrc = (category?.c_slotBannerImage as string) || undefined;

    // Transform any image URLs in the HTML banner to use DIS with WebP optimization
    const transformedBannerHtml = transformHtmlImageUrls((category.c_headerMenuBanner as string) || '', config);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={categoryHref(category)} prefetch="intent">
                {imageSrc ? (
                    // DynamicImage runs its own DIS transform, so the raw category image link is
                    // passed as `src` here (not pre-transformed via toImageUrl).
                    <DynamicImage
                        src={imageSrc}
                        alt={category.name}
                        widths={MEGA_MENU_BANNER_IMAGE_WIDTHS}
                        loading="lazy"
                        objectFit="contain"
                        className="w-full max-w-full max-h-[512px]"
                        imageProps={{ className: 'w-full max-w-full max-h-[512px]' }}
                    />
                ) : (
                    // oxlint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: transformedBannerHtml }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

/**
 * True when the resolved embedded component actually holds authored content in `regionId`.
 * A declared-but-empty region returns false so the panel can fall through to the banner.
 * Pure and exported so the region-vs-banner precedence can be unit-tested without mounting
 * the lazily-rendered dropdown.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function regionHasContent(resolved: ComponentWithComponentData | null, regionId: string): boolean {
    return (resolved?.regions?.find((r) => r.id === regionId)?.components?.length ?? 0) > 0;
}

/**
 * Right-column "featured content" for a top-level category's dropdown panel.
 *
 * Precedence: a populated embedded region (`region_<category.id>`) wins; otherwise the
 * legacy `c_headerMenuBanner`; otherwise nothing.
 *
 * A *declared but empty* region must NOT win over the banner and must NOT emit a labelled
 * `<aside>` — a complementary landmark with no content is screen-reader noise (WCAG 1.3.1).
 * Because the embedded component is streamed from the loader, whether a region actually
 * holds authored content is only known once the promise resolves, so the populated check
 * (`regionHasContent`) happens inside Await. Design-mode authoring of these regions runs
 * through the mini-PD component-preview route (page mode), not this slot, so no design-mode
 * branch is needed here.
 */
function MegaMenuFeaturedSlot({
    category,
    regionId,
    embeddedComponent,
    label,
}: {
    category: ShopperProducts.schemas['Category'];
    regionId: string | undefined;
    embeddedComponent: EmbeddedMegaMenuComponent;
    label: string;
}): ReactNode {
    const bannerSlot = hasBanner(category) ? (
        <aside className="self-stretch" aria-label={label}>
            <CategoryBanner category={category} />
        </aside>
    ) : null;

    // No region mapped for this category (or no embedded component was fetched):
    // the banner is the only possible featured content.
    if (!regionId || embeddedComponent === undefined) {
        return bannerSlot;
    }

    const renderResolved = (resolved: ComponentWithComponentData | null): ReactNode => {
        if (!regionHasContent(resolved, regionId)) {
            return bannerSlot;
        }
        return (
            <aside className="self-stretch" aria-label={label}>
                <EmbeddedComponentRegion component={resolved} regionId={regionId} />
            </aside>
        );
    };

    if (embeddedComponent instanceof Promise) {
        // Below-the-fold panel; the promise is fetched at route load and is almost always
        // resolved before the dropdown opens, so `fallback={null}` avoids a banner→region flash.
        return (
            <Suspense fallback={null}>
                <Await resolve={embeddedComponent} errorElement={bannerSlot}>
                    {renderResolved}
                </Await>
            </Suspense>
        );
    }
    return renderResolved(embeddedComponent);
}

function hasSubcategories(category: ShopperProducts.schemas['Category']): boolean {
    // RGBARRY-CUSTOMIZATION: baggallini wants a flyout/drawer for any L1 with at least one
    // online L2 (rather than the template default of requiring 2+), so single-child categories
    // like Travel still show their subcategory instead of collapsing to a plain leaf link.
    return (
        typeof category.onlineSubCategoriesCount === 'number' &&
        category.onlineSubCategoriesCount >= 1 &&
        Array.isArray(category.categories) &&
        category.categories.length >= 1
    );
}

function MobileMenuCategory({
    category: rawCategory,
    expandedCategories,
    onToggle,
    onNavigate,
}: {
    category: ShopperProducts.schemas['Category'];
    expandedCategories: Set<string>;
    onToggle: (categoryId: string) => void;
    onNavigate: () => void;
}): ReactElement {
    const { t } = useTranslation('header');
    const { trackNavClick } = useAnalytics();
    const categoryHref = useCategoryHref();
    const enrichedCategory = useSubCategory(rawCategory.id);
    const category = enrichedCategory ?? rawCategory;
    const hasChildren = hasSubcategories(category);
    const isExpanded = expandedCategories.has(category.id);

    // Renders L2 (level=1) as a bold uppercase header, L3+ (level>=2) as plain items,
    // both leading to their category page. Matches the desktop L2/L3 visual hierarchy.
    // `ancestors` carries the labels above the rendered level (L1 first) so nav_click can report
    // the full hierarchy (RGBARRY-CUSTOMIZATION, W-23789028, slice 4).
    const renderSubcategoryLinks = (
        subcategories: ShopperProducts.schemas['Category'][] | undefined,
        level = 1,
        ancestors: Array<string | undefined> = [category.name]
    ): ReactElement[] =>
        subcategories?.map((subcategory) => (
            <li key={subcategory.id}>
                <NavLink
                    to={categoryHref(subcategory)}
                    onClick={() => {
                        void trackNavClick(toNavLevels([...ancestors, subcategory.name]));
                        onNavigate();
                    }}
                    prefetch="intent"
                    className={cn(
                        'block py-2 hover:opacity-70 transition-opacity',
                        level === 1
                            ? 'text-sm font-bold uppercase tracking-wider mt-3 mb-1'
                            : 'text-sm font-normal text-header-foreground/80'
                    )}>
                    {subcategory.name}
                </NavLink>
                {subcategory.categories?.length ? (
                    <ul className="pl-2 space-y-0">
                        {renderSubcategoryLinks(subcategory.categories, level + 1, [...ancestors, subcategory.name])}
                    </ul>
                ) : null}
            </li>
        )) ?? [];

    // If L1 has children, tapping the row expands/collapses the drawer instead of navigating.
    // Leaf L1s (e.g. "Sale" with no subs) fall back to a direct link.
    return (
        <li>
            {hasChildren ? (
                <button
                    type="button"
                    onClick={() => onToggle(category.id)}
                    className="flex w-full items-center justify-between py-3 text-base font-medium hover:opacity-70 transition-opacity"
                    aria-label={
                        isExpanded
                            ? t('collapseCategory', {
                                  category: category.name,
                                  defaultValue: `Collapse ${category.name}`,
                              })
                            : t('expandCategory', {
                                  category: category.name,
                                  defaultValue: `Expand ${category.name}`,
                              })
                    }
                    aria-expanded={isExpanded}>
                    <span>{category.name}</span>
                    <ChevronDown
                        className={cn('size-5 transition-transform duration-200 shrink-0', {
                            'rotate-180': isExpanded,
                        })}
                    />
                </button>
            ) : (
                <NavLink
                    to={categoryHref(category)}
                    onClick={() => {
                        void trackNavClick(toNavLevels([category.name]));
                        onNavigate();
                    }}
                    prefetch="intent"
                    className="block py-3 text-base font-medium hover:opacity-70 transition-opacity">
                    {category.name}
                </NavLink>
            )}

            {hasChildren && isExpanded && (
                <ul className="pl-2 pb-2 space-y-0">{renderSubcategoryLinks(category.categories)}</ul>
            )}
        </li>
    );
}

/**
 * MobileMenuDropdown - Renders the mobile menu dropdown content with expandable subcategories
 * Uses absolute positioning (relative to header) to automatically appear below the header
 * regardless of header height changes. No hardcoded values needed.
 */
export function MobileMenuDropdown(): ReactElement | null {
    const context = useMobileMenu();
    const { t } = useTranslation('header');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    // Mount the category list only while the menu is open. Each MobileMenuCategory subscribes to the sub-category
    // store via useSubCategory (useSyncExternalStore). Keeping that subtree mounted while the menu is closed puts the
    // subscribers in the SSR/hydration tree, so the post-hydration store fill re-renders the header and cascades into
    // a whole-page flicker. Gating the mount on `isOpen` keeps the subscribers out of the initial render — matching
    // the desktop mega panel, whose subscribers live inside the lazily-mounted Radix content.
    if (!context || !context.isOpen) {
        return null;
    }

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    return (
        <div className="lg:hidden absolute left-0 right-0 top-full bg-header-background text-header-foreground shadow-lg z-40 max-h-[70vh] overflow-y-auto">
            <nav className="px-4 py-4" aria-label={t('mobileNavigation', 'Mobile navigation menu')}>
                <ul className="space-y-1">
                    {context.categories.map((category) => (
                        <MobileMenuCategory
                            key={category.id}
                            category={category}
                            expandedCategories={expandedCategories}
                            onToggle={toggleCategory}
                            onNavigate={context.close}
                        />
                    ))}
                </ul>
            </nav>
        </div>
    );
}

type EmbeddedMegaMenuComponent = ComponentWithComponentData | Promise<ComponentWithComponentData | null> | undefined;

interface ResponsiveNavigationMenuProps extends ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> {
    /**
     * Embedded mega-menu Page Designer component fetched by the route loader via
     * `fetchComponentWithComponentData({ componentId: 'mega-menu' })`. A top-level category whose
     * id matches a declared region (`region_${category.id}`, e.g. `region_womens`) renders that
     * region in its dropdown panel; merchants place content blocks (image, hero, etc.) into those
     * regions in Page Designer. Categories without a matching region fall back to the header banner.
     */
    embeddedComponent?: EmbeddedMegaMenuComponent;
    /** RGBARRY-CUSTOMIZATION: category names to exclude from the main nav and render separately. */
    pinnedCategoryNames?: string[];
    /**
     * RGBARRY-CUSTOMIZATION (W-23875749): promise of a `categoryId -> production SEO slug path` map
     * (from the `int_urlresolver_scapi` endpoint, resolved in the layout loader). Menu category links
     * prefer these slug paths so they match production; unresolved ids fall back to `/category/:id`.
     */
    categoryUrls?: Promise<CategoryUrlMap>;
}

/**
 * RGBARRY-CUSTOMIZATION: fill the stable desktop navigation slot while root data suspends,
 * preserving both its horizontal allocation and the responsive header-row dimensions.
 */
function ResponsiveNavigationMenuFallback(): ReactElement {
    return (
        <>
            <div aria-hidden="true" className="lg:hidden size-9 shrink-0" />
            <div aria-hidden="true" className="hidden lg:block h-full w-full" />
        </>
    );
}

/** Stable empty map so `useCategoryHref` consumers never see a fresh identity when unresolved. */
const EMPTY_CATEGORY_URL_MAP: CategoryUrlMap = {};

/**
 * ResponsiveNavigationMenu - A unified responsive navigation component
 *
 * This component uses CSS and Tailwind to adapt the same navigation structure
 * for both mobile and desktop:
 * - On mobile (< 1024px): Hamburger button with expandable vertical menu
 * - On desktop (>= 1024px): Horizontal mega menu with dropdown navigation
 *
 * The component renders a single navigation structure with responsive classes
 * controlling layout, visibility, and interaction patterns. This minimizes DOM bloat
 * while maintaining SSR compatibility.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @param props.embeddedComponent - Optional Page Designer 'mega-menu' component data
 * @returns A responsive navigation component with CSS-controlled responsive behavior
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
    embeddedComponent,
    // RGBARRY-CUSTOMIZATION: pinnedCategoryNames excludes categories from the main nav (unused by
    // default, kept so the brand nav can pin categories to a separate slot without an API change)
    pinnedCategoryNames = [],
    // RGBARRY-CUSTOMIZATION (W-23875749): resolved production slug paths for category links.
    categoryUrls,
}: ResponsiveNavigationMenuProps): ReactElement {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const { trackNavClick } = useAnalytics();
    const { scrolled, headerRef, searchOpen } = useHeaderScroll();

    // RGBARRY-CUSTOMIZATION: the mega menu is hover-driven, so it must not open (or stay open)
    // while the search dropdown is active — controlled `value` forces it closed and the
    // onValueChange guard drops hover-open requests that fire while search is open.
    const [openMenuValue, setOpenMenuValue] = useState('');

    // Track the header's live DOMRect in both pill and full-width modes so the flyout
    // always anchors to the actual bottom of the header element, not a CSS variable.
    const [headerRect, setHeaderRect] = useState<DOMRect | null>(null);
    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        setHeaderRect(el.getBoundingClientRect());
        const observer = new ResizeObserver(() => setHeaderRect(el.getBoundingClientRect()));
        observer.observe(el);
        const onScroll = () => setHeaderRect(el.getBoundingClientRect());
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', onScroll);
        };
    }, [headerRef]);
    const pillRect = scrolled ? headerRect : null;

    // RGBARRY-CUSTOMIZATION (W-23875749): keep the resolved root map available to imperative
    // top-level clicks. Direct node URLs win, but the map remains the fallback for older responses.
    const categoryUrlsRef = useRef<CategoryUrlMap>(EMPTY_CATEGORY_URL_MAP);

    // Handler for top-level category clicks
    const handleTopLevelClick = useCallback(
        (category: ShopperProducts.schemas['Category']) => {
            const href =
                readSeoUrl({ c_seoUrl: category.c_seoUrl }) ??
                categoryUrlsRef.current[category.id] ??
                routeHref(routes.category, { categoryId: category.id });
            void navigate(href);
        },
        [navigate]
    );

    // Element props generator
    const getElementProps = useCallback(
        ({
            level,
            category,
            isLeaf,
            path,
        }: {
            level: number;
            category: ShopperProducts.schemas['Category'];
            isLeaf?: boolean;
            // Ancestor categories above `category` (top-level first, excluding `category` itself).
            path: ReadonlyArray<ShopperProducts.schemas['Category']>;
        }) => {
            // level 0 = top-level (Women, Men) — the trigger
            // level 1 = L2 (Featured, Slippers, Shoes) — column header
            // level 2+ = L3+ (Bow Styles, Shop All, …) — items inside a column
            const isColumnHeader = level === 1;
            const isColumnItem = level >= 2;
            const isClickableParent = level === 0 && !isLeaf && category.id;

            // RGBARRY-CUSTOMIZATION (W-23789028, slice 4): emit a `nav_click` dataLayer event with
            // the full category hierarchy (ancestors + clicked category) so the GTM container's
            // nav_click trigger fires (SFRA parity). trackNavClick is consent-gated in useAnalytics.
            const fireNavClick = () => void trackNavClick(toNavLevels([...path.map((c) => c.name), category.name]));

            return {
                className: cn(
                    'leading-5',
                    !isColumnHeader && !isColumnItem && 'text-sm font-medium',
                    isColumnHeader &&
                        'text-sm font-bold uppercase tracking-wider mb-2 !px-0 hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/70 focus:!text-header-menu-foreground/70 transition-colors',
                    isColumnItem &&
                        'text-sm font-normal !px-0 !py-0.5 hover:!bg-transparent focus:!bg-transparent hover:!text-header-menu-foreground/60 focus:!text-header-menu-foreground/60 transition-colors'
                ),
                ...(isClickableParent
                    ? {
                          // Use onPointerDown instead of onClick for mouse-only navigation.
                          // This preserves keyboard accessibility: Enter/Space on the trigger
                          // expands the dropdown (Radix behavior), while mouse clicks navigate
                          // to the category page. Without this guard, keyboard users would be
                          // forced to navigate without being able to explore subcategories.
                          // nav_click fires only when the trigger actually navigates (mouse), not
                          // on keyboard-expand, which is not a navigation.
                          onPointerDown: (e: React.PointerEvent) => {
                              if (e.pointerType === 'mouse') {
                                  fireNavClick();
                                  handleTopLevelClick(category);
                              }
                          },
                      }
                    : {
                          // Leaf links (top-level leaf, L2 header, L3 item): fire on activation
                          // (mouse click or keyboard Enter) — each is a real navigation.
                          onClick: fireNavClick,
                      }),
            };
        },
        [handleTopLevelClick, trackNavClick]
    );

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer} fallback={<ResponsiveNavigationMenuFallback />}>
            {({ categories }) => {
                // RGBARRY-CUSTOMIZATION (W-23875749): unwrap the slug-path map here — inside
                // WithCategoryNavigationMenu's own Suspense boundary. The map is chained off the SAME
                // root-category promise this boundary already awaits (see _app.tsx loader), so by the
                // time this render function runs the root has resolved and the map settles in lockstep —
                // it never re-suspends the menu on the deferred sub-tree fetch. It is a pure projection
                // of the hook-stamped `c_seoUrl` and never rejects (possibly an empty map, which yields
                // id-based fallbacks).
                const categoryUrlMap = categoryUrls ? use(categoryUrls) : EMPTY_CATEGORY_URL_MAP;
                categoryUrlsRef.current = categoryUrlMap;

                // RGBARRY-CUSTOMIZATION: only collapse a top-level category to a plain leaf link
                // when it has zero online subcategories (template default was <= 1, which also
                // collapsed single-child L1s like Travel). See hasSubcategories() above.
                const flattened = categories.map((c) =>
                    typeof c.onlineSubCategoriesCount === 'number' && c.onlineSubCategoriesCount <= 0
                        ? { ...c, onlineSubCategoriesCount: 0, categories: [] }
                        : c
                );
                const pinnedSet = new Set(pinnedCategoryNames.map((n) => n.toLowerCase()));
                const mainCategories = pinnedSet.size
                    ? flattened.filter((c) => !pinnedSet.has((c.name ?? '').toLowerCase()))
                    : flattened;
                // Mobile shows all categories (pinned items appear last in mobile menu)
                const allMobileCategories = [
                    ...mainCategories,
                    ...flattened.filter((c) => pinnedSet.has((c.name ?? '').toLowerCase())),
                ];

                const mobileMenuContext: MobileMenuContextType = {
                    isOpen: mobileMenuOpen,
                    toggle: () => setMobileMenuOpen(!mobileMenuOpen),
                    close: () => setMobileMenuOpen(false),
                    categories: allMobileCategories,
                };

                const regionIdFor = (categoryId: string | undefined): string | undefined =>
                    resolveMegaMenuRegionId(categoryId, embeddedComponent !== undefined);

                return (
                    <CategoryHrefProvider value={categoryUrlMap}>
                        <MobileMenuContext.Provider value={mobileMenuContext}>
                            {/* Mobile: Hamburger button */}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={mobileMenuContext.toggle}
                                className="lg:hidden hover:bg-transparent hover:opacity-50 transition-opacity"
                                aria-label={mobileMenuOpen ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu')}
                                aria-expanded={mobileMenuOpen}>
                                {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
                            </Button>

                            {/* Desktop: Mega menu (always rendered, hidden on mobile with CSS) */}
                            <div className="hidden lg:flex h-full w-full items-center justify-end">
                                <CategoryNavigationMenu
                                    categories={mainCategories}
                                    delayDuration={0}
                                    value={searchOpen ? '' : openMenuValue}
                                    onValueChange={(value) => {
                                        if (!searchOpen) setOpenMenuValue(value);
                                    }}
                                    propsViewport={() =>
                                        scrolled && pillRect
                                            ? {
                                                  // Pill mode: flyout matches pill width and position
                                                  className:
                                                      'border-0 shadow-sm [--ui-radius:0px] !mt-0 [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none will-change-transform',
                                                  style: {
                                                      position: 'fixed',
                                                      top: pillRect.bottom,
                                                      marginTop: 0,
                                                      left: pillRect.left,
                                                      right: 'auto',
                                                      width: pillRect.width,
                                                  },
                                              }
                                            : {
                                                  // Full-width mode: anchor to live header bottom
                                                  className:
                                                      'border-0 shadow-sm [--ui-radius:0px] !mt-0 [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none will-change-transform',
                                                  style: {
                                                      position: 'fixed',
                                                      top: headerRect ? headerRect.bottom : 'var(--header-height)',
                                                      marginTop: 0,
                                                      left: 0,
                                                      right: 0,
                                                      width: '100%',
                                                  },
                                              }
                                    }
                                    propsContentContainer={() => ({
                                        className:
                                            '!p-0 !left-auto !right-auto !w-full md:!w-full !animate-none !transition-none',
                                    })}
                                    propsContent={({ category }) => {
                                        // RGBARRY-CUSTOMIZATION: right column shows a KVS region, live catalog
                                        // banner, or the static NAV_BANNERS fallback; pill-aware container.
                                        const hasRegion = regionIdFor(category.id) !== undefined;
                                        const staticBanner = NAV_BANNERS[(category?.name ?? '').toLowerCase()];
                                        const hasBannerContent =
                                            hasRegion || hasBanner(category) || Boolean(staticBanner);
                                        const l2Count = category?.categories?.length ?? 0;
                                        const isSingleCol = l2Count <= 1;
                                        return {
                                            className: cn(
                                                scrolled ? 'px-8 py-6' : 'max-w-[1280px] mx-auto px-6 lg:px-8 py-6',
                                                hasBannerContent &&
                                                    (isSingleCol
                                                        ? 'flex gap-16 items-start justify-center'
                                                        : 'grid md:grid-cols-[1fr_380px] items-start')
                                            ),
                                        };
                                    }}
                                    propsList={({ categories: subCategories, level }) => {
                                        if (level === 1) {
                                            // RGBARRY-CUSTOMIZATION: auto-size columns to content width. Multi-col lists
                                            // use ml-[min(303px,...)] to anchor the first column at the same x-position
                                            // as single-col flyouts (which are centered via flex justify-center on the
                                            // outer container). 303px = (1216 - single_col_text_width - 64 - 380) / 2
                                            // measured at the 1280px max-width panel. The min() prevents overflow on
                                            // narrower lg breakpoints (1024-1180px).
                                            const isMulti = subCategories.length > 1;
                                            return {
                                                style: {
                                                    gridTemplateColumns: `repeat(${subCategories.length}, auto)`,
                                                },
                                                className: cn(
                                                    'grid gap-x-8 p-0 w-fit',
                                                    isMulti ? 'ml-[min(303px,calc(100%-440px))]' : 'mx-auto'
                                                ),
                                            };
                                            // END RGBARRY-CUSTOMIZATION
                                        }
                                        if (level >= 2) {
                                            // L3 items stacked as a vertical list under each L2 column header
                                            return {
                                                className: 'flex flex-col gap-0 p-0 mt-0',
                                            };
                                        }
                                    }}
                                    propsListItem={() => undefined}
                                    propsElement={getElementProps}
                                    renderSlotListAfter={({ level, parent }) => {
                                        if (level !== 1 || !parent) return null;
                                        // The dropdown renders multiple complementary landmarks (one per open
                                        // category), so each <aside> needs a distinct accessible name for screen
                                        // reader users to tell them apart (WCAG 1.3.1). Categories with neither a
                                        // populated region nor a banner render nothing — an empty landmark would
                                        // only add screen reader noise (handled inside MegaMenuFeaturedSlot).
                                        const regionId = regionIdFor(parent.id);
                                        // RGBARRY-CUSTOMIZATION: fall back to the static NAV_BANNERS map when the
                                        // category has neither a KVS region nor a live catalog banner.
                                        const staticBanner = NAV_BANNERS[(parent.name ?? '').toLowerCase()];
                                        if (!regionId && !hasBanner(parent) && staticBanner) {
                                            return (
                                                <aside className="self-stretch pl-8 flex flex-col gap-3 min-w-[300px] max-w-[380px]">
                                                    {/* RGBARRY-CUSTOMIZATION: promotional header images link directly
                                                        to their brand-approved category landing pages. */}
                                                    <NavigationMenuLink asChild>
                                                        <NavLink
                                                            to={staticBanner.href}
                                                            prefetch="intent"
                                                            className="block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                                                            <img
                                                                src={staticBanner.imageUrl}
                                                                alt={staticBanner.altText}
                                                                className="w-full object-cover rounded-sm"
                                                            />
                                                        </NavLink>
                                                    </NavigationMenuLink>
                                                    {/* END RGBARRY-CUSTOMIZATION */}
                                                    <p className="text-sm text-[#5a5a5a] leading-relaxed">
                                                        {staticBanner.caption}
                                                    </p>
                                                </aside>
                                            );
                                        }
                                        return (
                                            <MegaMenuFeaturedSlot
                                                category={parent}
                                                regionId={regionId}
                                                embeddedComponent={embeddedComponent}
                                                label={t('featuredContent', {
                                                    category: parent.name,
                                                    defaultValue: `${parent.name} featured content`,
                                                })}
                                            />
                                        );
                                    }}
                                />
                            </div>

                            {/* Mobile: Menu dropdown (rendered here to be inside provider) */}
                            <MobileMenuDropdown />
                        </MobileMenuContext.Provider>
                    </CategoryHrefProvider>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
